import { useLazyQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { Issue } from "generated-graphql/graphql";
import { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

export const VALIDATE_SUBMISSION_WASTE_ACTIVITY_INPUT = gql(`
  query ValidateSubmissionWasteActivityInput($data: UpsertSubmissionWasteActivityInput!) {
    submissionWasteActivityInputValidator(data: $data) {
      ...issue
    }
  }
`);

/**
 * Live compliance-engine validation of the Waste Activity step's chosen waste
 * codes, following the stepper's validator-query idiom (the Facility Profile
 * step's `useFacilityRcraProfileInputValidation` inside `useValidatingForm`):
 * whenever the chosen code lists change, the standalone
 * `submissionWasteActivityInputValidator` query re-runs (debounced, no-cache)
 * and the returned issues are rendered inline at the fields. Validation is
 * advisory — the save itself never blocks on these issues; the mutation only
 * rejects on frozen submissions and malformed (empty-string) codes.
 *
 * The issue keys are the schema's field names (`federalWasteCodes` /
 * `stateWasteCodes`), so callers can split issues per field.
 *
 * On a failed validation request the last-known issues are deliberately
 * retained (fail-open-retain): stale advisory issues beat clearing real ones
 * on a transient network blip, and the next successful run replaces them.
 */
export function useWasteActivityCodeValidation({
  submissionId,
  federalWasteCodes,
  stateWasteCodes,
  skip,
}: {
  submissionId: string;
  /** Chosen codes; null while the prefill recommendation hasn't resolved yet. */
  federalWasteCodes: string[] | null;
  stateWasteCodes: string[] | null;
  /** Skip validation entirely (read-only step). */
  skip: boolean;
}): Issue[] {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [query] = useLazyQuery(VALIDATE_SUBMISSION_WASTE_ACTIVITY_INPUT, {
    fetchPolicy: "no-cache",
  });

  // Monotonic sequence so a slow earlier validation can never clobber the
  // issues of a later (current) one.
  const requestSeq = useRef(0);

  const validate = useDebouncedCallback(async (federal: string[], state: string[]) => {
    const seq = ++requestSeq.current;
    const { data } = await query({
      variables: {
        data: {
          submissionId,
          // Only the chosen codes are validated; the recommendation fields
          // just satisfy the shared upsert-input shape.
          federalWasteCodes: federal,
          stateWasteCodes: state,
          recommendedFederalWasteCodes: [],
          recommendedStateWasteCodes: [],
        },
      },
    });
    // No data means the request failed — keep the last-known issues (see the
    // fail-open-retain note above) rather than clearing them.
    if (seq === requestSeq.current && data) {
      setIssues(data.submissionWasteActivityInputValidator);
    }
  }, 400);

  // The chosen arrays have stable identities between edits (they come from
  // state / the Apollo cache, not per-render copies), so they can be effect
  // dependencies directly: this re-fires exactly when a code list changes.
  useEffect(() => {
    if (skip || federalWasteCodes == null || stateWasteCodes == null) {
      return;
    }
    void validate(federalWasteCodes, stateWasteCodes);
  }, [skip, federalWasteCodes, stateWasteCodes, validate]);

  return issues;
}
